import React, { useEffect, useState, useRef } from 'react';
import { FileText, Shield, CheckCircle, Loader2, Clock, PenLine } from 'lucide-react';
import { GenerationProgress } from '../services/documentGenerator';

interface GeneratingLoaderProps {
  documentType?: string;
  generationProgress?: GenerationProgress | null;
  onComplete?: () => void;
}

const stages = [
  {
    icon: FileText,
    title: 'Preparing Document',
    description: 'Analyzing your form data and requirements',
  },
  {
    icon: Shield,
    title: 'Applying Legal Framework',
    description: 'Incorporating UK housing law and regulatory standards',
  },
  {
    icon: PenLine,
    title: 'Writing Agreement',
    description: 'Creating your comprehensive legal document',
  },
  {
    icon: CheckCircle,
    title: 'Finalizing',
    description: 'Formatting and preparing for review',
  },
];

export const GeneratingLoader: React.FC<GeneratingLoaderProps> = ({
  documentType,
  generationProgress,
}) => {
  const [progress, setProgress] = useState(0);
  const [currentStage, setCurrentStage] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTimeRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setInterval>>();
  const isCompleteRef = useRef(false);

  useEffect(() => {
    startTimeRef.current = Date.now();

    timerRef.current = setInterval(() => {
      if (!isCompleteRef.current) {
        setElapsedTime(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!generationProgress) return;

    if (generationProgress.status === 'complete') {
      isCompleteRef.current = true;
      if (timerRef.current) clearInterval(timerRef.current);
      setCurrentStage(3);
      setProgress(100);
      return;
    }

    if (generationProgress.status === 'generating') {
      setCurrentStage(0);
      setProgress(5);
      return;
    }

    if (generationProgress.status === 'streaming') {
      const streamed = generationProgress.streamedChars || 0;
      const estimated = generationProgress.estimatedTotalChars || 1;
      const ratio = Math.min(streamed / estimated, 1);
      const streamProgress = Math.round(10 + ratio * 85);

      setProgress(streamProgress);

      if (ratio < 0.02) {
        setCurrentStage(1);
      } else if (ratio < 0.9) {
        setCurrentStage(2);
      } else {
        setCurrentStage(3);
      }
    }
  }, [generationProgress]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatWords = () => {
    if (!generationProgress?.streamedChars) return null;
    const approxWords = Math.round(generationProgress.streamedChars / 5.5);
    if (approxWords < 1000) return `${approxWords} words`;
    return `${(approxWords / 1000).toFixed(1)}k words`;
  };

  const isMultiPart = (generationProgress?.totalParts ?? 1) > 1;
  const currentPart = generationProgress?.currentPart ?? 1;
  const totalParts = generationProgress?.totalParts ?? 1;

  return (
    <div className="fixed inset-0 bg-gray-50 flex items-center justify-center z-50">
      <div className="max-w-lg w-full mx-4">
        <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm border border-gray-200">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-900 rounded-2xl mb-4">
              <Loader2 className="w-7 h-7 text-white animate-spin" />
            </div>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-1">
              Writing Your Agreement
            </h2>
            <p className="text-gray-500 text-sm">
              {isMultiPart
                ? `Writing in multiple sections for completeness`
                : 'This may take a few minutes'}
            </p>
            {documentType && (
              <div className="mt-3 inline-block bg-gray-100 rounded-lg px-3 py-1.5">
                <p className="text-gray-700 text-xs font-medium">
                  {documentType}
                </p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-medium text-gray-500">
                {isMultiPart ? `Section ${currentPart} of ~${totalParts}` : 'Progress'}
              </span>
              <div className="flex items-center gap-3">
                {formatWords() && (
                  <span className="text-xs text-gray-400">{formatWords()}</span>
                )}
                <span className="text-xs font-semibold text-gray-900">{Math.round(progress)}%</span>
              </div>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gray-900 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            {isMultiPart && (
              <div className="flex gap-1 mt-2">
                {Array.from({ length: totalParts }, (_, i) => (
                  <div
                    key={i}
                    className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                      i + 1 < currentPart
                        ? 'bg-gray-900'
                        : i + 1 === currentPart
                          ? 'bg-gray-900'
                          : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1 mb-6">
            {stages.map((stage, index) => {
              const isActive = index === currentStage;
              const isComplete = index < currentStage;
              const StageIcon = stage.icon;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-300 ${
                    isActive ? 'bg-gray-50' : ''
                  }`}
                >
                  <div className="flex-shrink-0">
                    {isComplete ? (
                      <div className="w-7 h-7 bg-gray-200 rounded-lg flex items-center justify-center">
                        <CheckCircle className="w-4 h-4 text-gray-900" />
                      </div>
                    ) : isActive ? (
                      <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center">
                        <StageIcon className="w-4 h-4 text-white" />
                      </div>
                    ) : (
                      <div className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center">
                        <StageIcon className="w-4 h-4 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium transition-colors duration-300 ${
                      isComplete ? 'text-gray-900' : isActive ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {stage.title}
                    </p>
                    {isActive && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {generationProgress?.message || stage.description}
                      </p>
                    )}
                  </div>
                  {isComplete && (
                    <span className="text-xs text-gray-900 font-medium">Done</span>
                  )}
                  {isActive && (
                    <div className="w-1.5 h-1.5 bg-gray-900 rounded-full animate-pulse" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <div className="flex items-center gap-1.5 text-xs text-gray-400">
              <Clock className="w-3.5 h-3.5" />
              <span>Elapsed: {formatTime(elapsedTime)}</span>
            </div>
            <p className="text-xs text-gray-400">
              Do not close this window
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
